import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../common/block-text/block-text.component';
import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { InlineMediaV2Directive } from '../../base-template-editor-v2/inline-edit/inline-media-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import { MomentsInitModel, MomentsInitVariables, MomentsModel, MomentsVariables } from '../../../../models/email-template/moments.interface';

/**
 * Mockup-styled Moments block - a drop-in replacement for app-moments in
 * block-renderer-v2 (same [block]/[isPreview] inputs and outputs), so it
 * covers both the center-canvas read-only summary (isPreview=true) and the
 * right-panel edit form (isPreview=false). Reuses the real, unmodified
 * app-block-text (image upload/segment/validation), app-field-editor-v2 and
 * app-pin sub-components and the exact same MomentsVariables field names -
 * only the surrounding layout is new. Same desktop/mobile single full-width
 * image pattern as Main Banner (see main-banner-v2) - no heading/text/CTA,
 * matching v1's own content (image only).
 */
@Component({
  selector: 'app-moments-v2',
  imports: [CommonModule, ReactiveFormsModule, BlockTextComponent, FieldEditorV2Component, InlineMediaV2Directive],
  templateUrl: './moments-v2.component.html',
  styleUrl: './moments-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MomentsComponentV2 extends BaseComponent implements OnInit {
  @Input() block: MomentsModel = new MomentsInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;
  /** Center-canvas summary shows the desktop OR mobile image to match the
   *  canvas's own device toggle, not both stacked (see main-banner-v2). */
  @Input() deviceMode: 'desktop' | 'mobile' = 'desktop';

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new MomentsInitVariables();
    }
  }

  imageDesktopPlaceholder = 'Desktop Image';
  imageMobilePlaceholder = 'Mobile Image';
  hrefPlaceholder = 'Enter Image Redirect link';
  altPlaceholder = 'Enter Image Alt Text';

  protected desktopImageAsUpload = true;
  protected mobileImageAsUpload = true;

  protected toggleDesktopImageMode(): void {
    this.desktopImageAsUpload = !this.desktopImageAsUpload;
  }

  protected toggleMobileImageMode(): void {
    this.mobileImageAsUpload = !this.mobileImageAsUpload;
  }

  /** The image matching the canvas's current device toggle - undefined if
   *  that specific slot (desktop/mobile) hasn't been uploaded, even when the
   *  other one has (see main-banner-v2's currentDeviceImage). */
  get currentDeviceImage(): string | undefined {
    return this.block.variables?.[this.currentDeviceImageKey];
  }

  /** The variable the canvas image slot shows and replaces - an upload in
   *  mobile view replaces the mobile image, not the desktop one. */
  get currentDeviceImageKey(): 'MomentsImageMobile' | 'MomentsImageDesktop' {
    return this.deviceMode === 'mobile' ? 'MomentsImageMobile' : 'MomentsImageDesktop';
  }

  /** The redirect link opened by clicking the preview image - same treatment
   *  as Main Banner's previewHref: v1's exact helper.getFullUrl(...), plus a
   *  segment query param when a segment is selected. */
  get previewHref(): string {
    const base = this.helper.getFullUrl(this.block.variables?.['MomentsHref']);
    const segment = this.block.variables?.['MomentsHrefSegmentRegex'];
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof MomentsVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
